import { useState, useEffect } from 'react';
import {
  extractReturnUrl,
  storeReturnUrl,
  clearStoredReturnUrl,
  isValidReturnUrl as validateReturnUrl,
  sanitizeReturnUrl,
  handleAuthenticationRedirect
} from '../utils/urlUtils';

/**
 * Hook for managing return URL state and handling redirects
 */
export const useReturnUrl = () => {
  const [returnUrl, setReturnUrl] = useState<string | null>(null);
  const [isValidUrl, setIsValidUrl] = useState<boolean>(true);
  const [returnUrlError, setReturnUrlError] = useState<string | null>(null);

  /**
   * Initialize return URL from URL parameters or session storage
   */
  useEffect(() => {
    // First, try to get return URL from current page parameters
    const urlParam = extractReturnUrl();
    
    if (urlParam) {
      const sanitized = sanitizeReturnUrl(urlParam);
      const valid = validateReturnUrl(sanitized);
      
      if (valid) {
        setReturnUrl(sanitized);
        storeReturnUrl(sanitized);
        setIsValidUrl(true);
        setReturnUrlError(null);
      } else {
        setIsValidUrl(false);
        setReturnUrlError('errors.returnUrl.notAllowedDomain');
        console.warn('Invalid return URL detected:', urlParam);
      }
    } else {
      // Return URL is mandatory - show error if not provided
      setIsValidUrl(false);
      setReturnUrlError('errors.returnUrl.required');
      console.warn('No return URL provided - this parameter is mandatory');
    }
  }, []);

  /**
   * Set a new return URL
   */
  const setNewReturnUrl = (url: string | null) => {
    if (!url) {
      setReturnUrl(null);
      clearStoredReturnUrl();
      setIsValidUrl(true);
      setReturnUrlError(null);
      return;
    }

    const sanitized = sanitizeReturnUrl(url);
    const valid = validateReturnUrl(sanitized);

    if (valid) {
      setReturnUrl(sanitized);
      storeReturnUrl(sanitized);
      setIsValidUrl(true);
      setReturnUrlError(null);
    } else {
      setIsValidUrl(false);
      setReturnUrlError('errors.returnUrl.notAllowedDomain');
      console.warn('Attempted to set invalid return URL:', url);
    }
  };

  /**
   * Clear the current return URL
   */
  const clearReturnUrl = () => {
    setReturnUrl(null);
    clearStoredReturnUrl();
    setIsValidUrl(true);
    setReturnUrlError(null);
  };

  /**
   * Handle redirect after successful authentication
   */
  const redirectAfterAuth = (token: string) => {
    handleAuthenticationRedirect(token, returnUrl || undefined);
  };

  /**
   * Get the current return URL for display purposes
   */
  const getDisplayUrl = (): string | null => {
    if (!returnUrl) return null;
    
    try {
      const url = new URL(returnUrl);
      return `${url.hostname}${url.pathname}`;
    } catch (error) {
      return returnUrl;
    }
  };

  return {
    returnUrl,
    isValidReturnUrl: isValidUrl,
    returnUrlError,
    setReturnUrl: setNewReturnUrl,
    clearReturnUrl,
    redirectAfterAuth,
    getDisplayUrl,
    hasReturnUrl: Boolean(returnUrl)
  };
};